import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMixerModel } from '../../audio/useMixerModel';
import { AuthorText } from '../../components/AuthorText';
import { Credits } from '../../components/Credits';
import { Steve } from '../../components/Steve';
import { VuMeter } from '../../components/VuMeter';
import { S, faq, t, tList, type Leaf } from '../../i18n/strings';
import { cx } from '../../lib/cx';
import { usePrefersReducedMotion } from '../../lib/motion';
import { readString, writeString } from '../../lib/storage';
import { useActions, useSteve } from '../../state/context';
import { useAudioText, useGuestbookLines } from '../../state/derived';
import { PaintMenuBar, type Menu } from './PaintMenuBar';
import styles from './paint.module.css';
import {
  isHexColor,
  isSize,
  normalizeHex,
  PALETTE,
  SIZES,
  type Size,
} from './drawing';
import { downloadBlob, rasterise, scribbleFileName } from './rasterise';
import { DEFAULT_TOOL, isTool, TOOL_INFO, TOOLS, usesPointer, type Tool } from './tools';
import { NavIcon, PickerIcon } from './ToolIcons';
import { usePaintLayer, type Point, type Rect } from './usePaintLayer';

/** Fond de la toile, repris tel quel du handoff (papier). */
const PAPER = '#fffdf5';

/** Réglages conservés d'une visite à l'autre — jamais le dessin lui-même. */
const PREFS_KEY = 'steve.paint.prefs';
const PREFS_MAX = 300;

type Dialog = 'about' | 'help' | 'new' | 'quit' | 'colors';

interface Prefs {
  readonly tool: Tool;
  readonly primary: string;
  readonly secondary: string | null;
  readonly size: Size;
}

function readPrefs(): Prefs {
  const fallback: Prefs = { tool: DEFAULT_TOOL, primary: '#16130f', secondary: null, size: 3 };
  const raw = readString(PREFS_KEY, PREFS_MAX);
  if (raw === null) return fallback;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return fallback;
    const record = parsed as Record<string, unknown>;
    return {
      // Chaque valeur relue du stockage est re-validée : une clé trafiquée ne
      // doit produire ni outil inconnu, ni couleur injectée dans le canvas.
      tool: isTool(record['tool']) ? record['tool'] : fallback.tool,
      primary:
        typeof record['primary'] === 'string' && isHexColor(record['primary'])
          ? normalizeHex(record['primary'])
          : fallback.primary,
      secondary:
        typeof record['secondary'] === 'string' && isHexColor(record['secondary'])
          ? normalizeHex(record['secondary'])
          : null,
      size: isSize(record['size']) ? record['size'] : fallback.size,
    };
  } catch {
    return fallback;
  }
}

/** Curseur de la gomme : un carré blanc bordé de noir, à sa vraie taille. */
function eraserCursor(size: number): string {
  const side = size * 3;
  const box = side + 2;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${String(box)}" height="${String(box)}">` +
    `<rect x="0.5" y="0.5" width="${String(side)}" height="${String(side)}" fill="%23fff" stroke="%23000"/></svg>`;
  return `url("data:image/svg+xml,${svg.replace(/"/g, "'").replace(/#/g, '%23')}") ${String(box / 2)} ${String(box / 2)}, cell`;
}

/** Skin 4 — Paint : le site comme un dessin en cours, et de quoi dessiner dessus. */
export function PaintSkin(): ReactNode {
  const { lang, seen, draft, followCursor } = useSteve();
  const actions = useActions();
  const mixer = useMixerModel();
  const audio = useAudioText();
  const guestbook = useGuestbookLines();
  const reducedMotion = usePrefersReducedMotion();
  const questions = faq(lang);

  const initial = useRef<Prefs | null>(null);
  initial.current ??= readPrefs();
  const [tool, setTool] = useState<Tool>(initial.current.tool);
  const [primary, setPrimary] = useState(initial.current.primary);
  const [secondary, setSecondary] = useState<string | null>(initial.current.secondary);
  const [size, setSize] = useState<Size>(initial.current.size);

  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [textDraft, setTextDraft] = useState<(Point & { value: string }) | null>(null);
  const [hasSelection, setHasSelection] = useState(false);
  const [message, setMessage] = useState<Leaf | null>(null);
  const [hexDraft, setHexDraft] = useState(initial.current.primary);

  const contentRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const coordsRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLInputElement>(null);
  const shapeRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    writeString(PREFS_KEY, JSON.stringify({ tool, primary, secondary, size }));
  }, [tool, primary, secondary, size]);

  // Le champ de l'outil texte doit prendre le focus dès qu'il est posé : sinon
  // la frappe file dans la page, où elle croiserait les mots secrets du site.
  const textPlaced = textDraft === null ? null : `${String(textDraft.x)},${String(textDraft.y)}`;
  useEffect(() => {
    if (textPlaced !== null) textRef.current?.focus();
  }, [textPlaced]);

  const onCoords = useCallback(
    (point: Point | null): void => {
      const node = coordsRef.current;
      if (!node) return;
      node.textContent =
        point === null
          ? ''
          : t(S.paint.chrome.status_coords, lang, {
              x: Math.round(point.x),
              y: Math.round(point.y),
            });
    },
    [lang],
  );

  const onShape = useCallback(
    (rect: Rect | null): void => {
      const node = shapeRef.current;
      if (!node) return;
      node.textContent =
        rect === null
          ? ''
          : t(S.paint.status.shape, lang, { w: Math.round(rect.w), h: Math.round(rect.h) });
    },
    [lang],
  );

  const paint = usePaintLayer({
    tool,
    primary,
    secondary,
    size,
    reducedMotion,
    onPick: setPrimary,
    onTextPlace: (point) => {
      setTextDraft({ ...point, value: '' });
    },
    onCoords,
    onShape,
    onSelection: (rect) => {
      setHasSelection(rect !== null && rect.w > 2 && rect.h > 2);
    },
  });

  // Vrai dès que la couche capte le pointeur — sélection comprise.
  const drawing = usesPointer(tool);

  // ── Enregistrement du gribouillage ──────────────────────────────────────
  const saveScribble = useCallback((): void => {
    const content = contentRef.current;
    const layer = paint.layerRef.current;
    if (!content || !layer) return;
    setMessage(S.paint.status.saving);
    void rasterise(content, layer, PAPER)
      .then(({ blob, withSite }) => {
        downloadBlob(blob, scribbleFileName());
        setMessage(withSite ? S.paint.status.saved : S.paint.status.save_failed);
      })
      .catch(() => {
        setMessage(S.paint.status.save_failed);
      });
  }, [paint.layerRef]);

  const openImage = useCallback(
    (file: File | undefined): void => {
      if (!file) return;
      void paint.drawImageFile(file).then((ok) => {
        if (!ok) setMessage(S.paint.status.image_failed);
      });
    },
    [paint],
  );

  // ── Menus ───────────────────────────────────────────────────────────────
  const menus = useMemo<readonly Menu[]>(
    () => [
      {
        id: 'file',
        label: S.paint.chrome.menu_file,
        items: [
          {
            id: 'new',
            label: S.paint.menus.file_new,
            onSelect: () => {
              setDialog('new');
            },
          },
          {
            id: 'open',
            label: S.paint.menus.file_open,
            onSelect: () => {
              fileRef.current?.click();
            },
          },
          { id: 'save', label: S.paint.menus.file_save, onSelect: saveScribble },
          {
            id: 'quit',
            label: S.paint.menus.file_quit,
            onSelect: () => {
              setDialog('quit');
            },
          },
        ],
      },
      {
        id: 'edit',
        label: S.paint.chrome.menu_edit,
        items: [
          {
            id: 'undo',
            label: S.paint.menus.edit_undo,
            shortcut: 'Ctrl+Z',
            disabled: !paint.canUndo,
            onSelect: paint.undo,
          },
          {
            id: 'redo',
            label: S.paint.menus.edit_redo,
            shortcut: 'Ctrl+Y',
            disabled: !paint.canRedo,
            onSelect: paint.redo,
          },
          { id: 'clear', label: S.paint.menus.edit_clear, onSelect: paint.clear },
        ],
      },
      {
        id: 'steve',
        label: S.paint.chrome.menu_steve,
        items: [
          {
            id: 'follow',
            label: S.paint.menus.steve_follow,
            checked: followCursor,
            onSelect: actions.toggleFollower,
          },
          {
            id: 'stamp',
            label: S.paint.menus.steve_stamp,
            onSelect: () => {
              setTool('stamp');
            },
          },
          {
            id: 'reroll',
            label: S.paint.menus.steve_reroll,
            onSelect: () => {
              actions.cycleSkin(0);
            },
          },
        ],
      },
      {
        id: 'colors',
        label: S.paint.chrome.menu_colors,
        items: [
          {
            id: 'edit-colors',
            label: S.paint.menus.colors_edit,
            onSelect: () => {
              setHexDraft(primary);
              setDialog('colors');
            },
          },
        ],
      },
      {
        id: 'help',
        label: S.paint.chrome.menu_help,
        items: [
          {
            id: 'help',
            label: S.paint.menus.help_f1,
            shortcut: 'F1',
            onSelect: () => {
              setDialog('help');
            },
          },
          {
            id: 'about',
            label: S.paint.menus.help_about,
            onSelect: () => {
              setDialog('about');
            },
          },
        ],
      },
    ],
    [actions, followCursor, paint.canRedo, paint.canUndo, paint.clear, paint.redo, paint.undo, primary, saveScribble],
  );

  // ── Clavier ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target;
      const typing =
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);

      if (event.key === 'Escape') {
        if (dialog !== null) setDialog(null);
        else if (openMenu !== null) setOpenMenu(null);
        else if (textDraft !== null) setTextDraft(null);
        // Dernier recours : on repasse en navigation, le site redevient
        // entièrement cliquable. C'est la sortie de secours du handoff.
        else setTool('nav');
        return;
      }
      if (event.key === 'F1') {
        event.preventDefault();
        setDialog('help');
        return;
      }
      if (typing) return;

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        paint.undo();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y') {
        event.preventDefault();
        paint.redo();
        return;
      }
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (hasSelection) {
          event.preventDefault();
          paint.eraseSelection();
          setHasSelection(false);
        }
        return;
      }
      const index = Number.parseInt(event.key, 10);
      if (Number.isInteger(index) && index >= 1 && index <= SIZES.length) {
        const next = SIZES[index - 1];
        if (next !== undefined) setSize(next);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [dialog, hasSelection, openMenu, paint, textDraft]);

  // Un message d'état s'efface tout seul : la barre revient à sa phrase.
  useEffect(() => {
    if (message === null) return;
    const id = window.setTimeout(() => {
      setMessage(null);
    }, 4000);
    return () => {
      window.clearTimeout(id);
    };
  }, [message]);

  const colorNames = tList(S.paint.colors.names, lang);
  const statusText =
    message !== null
      ? t(message, lang)
      : drawing
        ? t(TOOL_INFO[tool].label, lang)
        : t(S.paint.chrome.status_hint, lang);

  return (
    <div
      className={styles.root}
      onPointerDown={(event) => {
        // Un clic hors des menus les referme, comme dans le vrai logiciel.
        if (openMenu !== null && !(event.target as HTMLElement).closest(`.${styles.menuBar ?? ''}`)) {
          setOpenMenu(null);
        }
      }}
    >
      <div className={styles.window}>
        <div className={styles.titleBar}>
          <span>
            {t(paint.dirty ? S.paint.chrome.window_title_dirty : S.paint.chrome.window_title, lang)}
          </span>
          <div className={styles.titleButtons} aria-hidden="true">
            <span className={styles.titleButton}>_</span>
            <span className={styles.titleButton}>□</span>
            <span className={styles.titleButton}>×</span>
          </div>
        </div>

        <PaintMenuBar menus={menus} open={openMenu} lang={lang} onOpen={setOpenMenu} />

        <div className={styles.workspace}>
          <div className={styles.sidebar}>
            <div className={styles.tools} role="toolbar" aria-label={t(S.paint.tools.palette, lang)}>
              {TOOLS.map((name) => (
                <button
                  key={name}
                  type="button"
                  className={cx(styles.tool, tool === name && styles.toolActive)}
                  aria-pressed={tool === name}
                  aria-label={t(TOOL_INFO[name].label, lang)}
                  title={t(TOOL_INFO[name].label, lang)}
                  onClick={() => {
                    setTool(name);
                    setTextDraft(null);
                  }}
                >
                  {name === 'nav' ? (
                    <NavIcon className={styles.toolArt} />
                  ) : name === 'picker' ? (
                    <PickerIcon className={styles.toolArt} />
                  ) : (
                    <span aria-hidden="true">{TOOL_INFO[name].glyph}</span>
                  )}
                </button>
              ))}
            </div>

            <div className={styles.sizes} role="group" aria-label={t(S.paint.tools.sizes, lang)}>
              {SIZES.map((value, index) => (
                <button
                  key={value}
                  type="button"
                  className={cx(styles.sizeButton, size === value && styles.sizeActive)}
                  aria-pressed={size === value}
                  aria-label={`${t(S.paint.tools.sizes, lang)} ${String(value)}`}
                  title={`${String(value)} px — ${String(index + 1)}`}
                  onClick={() => {
                    setSize(value);
                  }}
                >
                  <span className={styles.sizeBar} style={{ height: `${String(value)}px` }} />
                </button>
              ))}
            </div>
          </div>

          {/*
            La zone défile, la feuille porte le dessin : les calques suivent la
            hauteur du contenu, pas celle de l'écran.
          */}
          <div className={styles.canvasArea}>
            <div className={styles.sheet} ref={paint.areaRef}>
              <div className={styles.content} ref={contentRef}>
                <header className={styles.heading}>
                  <h1 className={styles.title}>{t(S.paint.canvas.title, lang)}</h1>
                  <div className={styles.marker} aria-hidden="true" />
                  <p className={styles.subtitle}>{t(S.paint.canvas.subtitle, lang)}</p>
                </header>

                <div className={styles.row}>
                  <section className={cx(styles.card, styles.cardSteve)}>
                    <div className={styles.steveHolder}>
                      <Steve
                        bobDuration="3.2s"
                        onClick={actions.bump}
                        label={t(S.common.title, lang)}
                      />
                    </div>
                    <p className={styles.caption}>{t(S.paint.canvas.here_he_is, lang)}</p>
                  </section>

                  <section className={cx(styles.card, styles.cardWho)}>
                    <h2 className={styles.cardTitle}>{t(S.paint.canvas.who_title, lang)}</h2>
                    <p className={styles.paragraph}>{t(S.y2k.who_p1, lang)}</p>
                    <p className={styles.paragraph}>
                      <AuthorText linkClassName={styles.footerLink}>
                        {t(S.paint.canvas.who_p2, lang)}
                      </AuthorText>
                    </p>
                    <div className={styles.songRow}>
                      <button type="button" className={styles.button} onClick={actions.toggleAudio}>
                        {audio.label}
                      </button>
                      <span className={styles.hint} role="status">
                        {audio.hint}
                      </span>
                    </div>
                  </section>
                </div>

                <div className={styles.numbers}>
                  <section className={cx(styles.card, styles.cardNumber)}>
                    <span className={styles.numberLabel}>{t(S.common.seen_today, lang)}</span>
                    <span className={styles.number}>{seen}</span>
                  </section>
                  <section className={cx(styles.card, styles.cardNumber)}>
                    <span className={styles.numberLabel}>{t(S.paint.canvas.legs, lang)}</span>
                    <span className={styles.number}>2</span>
                  </section>
                  <section className={cx(styles.card, styles.cardNumber)}>
                    <span className={styles.numberLabel}>{t(S.common.orange_pct, lang)}</span>
                    <span className={styles.number}>100%</span>
                  </section>
                </div>

                <section className={cx(styles.card, styles.cardMixer)}>
                  <h2 className={styles.cardTitle}>{t(S.paint.canvas.mixer_title, lang)}</h2>
                  <VuMeter className={styles.vu} barClassName={styles.vuBar} />

                  <div className={styles.pads}>
                    {mixer.pads.map((pad) => (
                      <button key={pad.index} type="button" className={styles.pad} onClick={pad.onHit}>
                        <span>{pad.label}</span>
                        <span className={styles.padTime}>{pad.timeLabel}</span>
                      </button>
                    ))}
                  </div>

                  <div className={styles.buttons}>
                    <button type="button" className={styles.button} onClick={mixer.onToggleHold}>
                      {mixer.holdLabel}
                    </button>
                    <button type="button" className={styles.button} onClick={mixer.onStop}>
                      {mixer.stopLabel}
                    </button>
                    <button type="button" className={styles.button} onClick={mixer.onToggleWobble}>
                      {mixer.wobbleLabel}
                    </button>
                  </div>

                  {mixer.sliders.map((slider) => (
                    <div key={slider.key} className={styles.slider}>
                      <label className={styles.sliderLabel} htmlFor={`paint-${slider.key}`}>
                        {slider.label}
                      </label>
                      <input
                        id={`paint-${slider.key}`}
                        type="range"
                        className={styles.sliderInput}
                        min={slider.min}
                        max={slider.max}
                        step={slider.step}
                        value={slider.value}
                        onChange={(event) => {
                          slider.onChange(event.currentTarget.valueAsNumber);
                        }}
                      />
                      <output className={styles.sliderValue} htmlFor={`paint-${slider.key}`}>
                        {slider.valueLabel}
                      </output>
                    </div>
                  ))}

                  <div className={styles.buttons}>
                    {mixer.presets.map((preset) => (
                      <button
                        key={preset.name}
                        type="button"
                        className={styles.button}
                        onClick={preset.onSelect}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </section>

                <section className={cx(styles.card, styles.cardFaq)}>
                  <h2 className={styles.cardTitle}>{t(S.paint.canvas.faq_title, lang)}</h2>
                  <dl className={styles.faq}>
                    {questions.map((item) => (
                      <div key={item.question}>
                        <dt className={styles.question}>{item.question}</dt>
                        <dd className={styles.answer}>{item.answer}</dd>
                      </div>
                    ))}
                  </dl>
                </section>

                <section className={cx(styles.card, styles.cardGuestbook)}>
                  <h2 className={styles.cardTitle}>{t(S.paint.canvas.guestbook_title, lang)}</h2>
                  <ul className={styles.entries}>
                    {guestbook.map((entry) => (
                      <li key={entry.id} className={styles.entry}>
                        <span className={styles.who}>{entry.who}</span> — {entry.text}
                      </li>
                    ))}
                  </ul>
                  <div className={styles.compose}>
                    <input
                      type="text"
                      className={styles.input}
                      value={draft}
                      placeholder={t(S.guestbook.placeholder, lang)}
                      aria-label={t(S.guestbook.placeholder, lang)}
                      onChange={(event) => {
                        actions.setDraft(event.currentTarget.value);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') actions.postEntry();
                      }}
                    />
                    <button type="button" className={styles.button} onClick={actions.postEntry}>
                      {t(S.paint.canvas.send, lang)}
                    </button>
                  </div>
                </section>

                <footer className={styles.footer}>
                  <Credits className={styles.credits} linkClassName={styles.footerLink} unofficial />
                  <span>{t(S.common.egg_hint, lang)}</span>
                </footer>
              </div>

              {/* Calque de dessin : au-dessus du site, sous la chrome. */}
              <canvas ref={paint.layerRef} className={styles.layer} aria-hidden="true" tabIndex={-1} />
              <canvas
                ref={paint.overlayRef}
                className={cx(styles.overlay, drawing && styles.overlayActive)}
                aria-hidden="true"
                tabIndex={-1}
                style={{ cursor: tool === 'eraser' ? eraserCursor(size) : TOOL_INFO[tool].cursor }}
                onPointerDown={paint.onPointerDown}
                onPointerMove={paint.onPointerMove}
                onPointerUp={paint.onPointerUp}
                onPointerCancel={paint.onPointerUp}
                onPointerLeave={() => {
                  onCoords(null);
                }}
              />

              {textDraft && (
                <input
                  ref={textRef}
                  type="text"
                  className={styles.textInput}
                  style={{ left: `${String(textDraft.x)}px`, top: `${String(textDraft.y)}px` }}
                  value={textDraft.value}
                  placeholder={t(S.paint.text_tool.placeholder, lang)}
                  aria-label={t(S.paint.tools.text, lang)}
                  onChange={(event) => {
                    setTextDraft({ ...textDraft, value: event.currentTarget.value });
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      paint.commitText(textDraft.value, textDraft);
                      setTextDraft(null);
                    }
                    if (event.key === 'Escape') setTextDraft(null);
                  }}
                  onBlur={() => {
                    paint.commitText(textDraft.value, textDraft);
                    setTextDraft(null);
                  }}
                />
              )}
            </div>
          </div>
        </div>

        <div className={styles.statusBar}>
          <div className={styles.swatchFrame}>
            <span
              className={styles.swatchPair}
              aria-label={`${t(S.paint.colors.primary, lang)} ${primary}`}
              title={`${t(S.paint.colors.primary, lang)} ${primary}`}
            >
              <span className={styles.swatchBack} style={{ background: secondary ?? PAPER }} />
              <span className={styles.swatchFront} style={{ background: primary }} />
            </span>
          </div>

          <div
            className={styles.palette}
            role="group"
            aria-label={t(S.paint.colors.group, lang)}
            onContextMenu={(event) => {
              event.preventDefault();
            }}
          >
            {PALETTE.map((color, index) => (
              <button
                key={color}
                type="button"
                className={cx(styles.swatch, primary === color && styles.swatchActive)}
                style={{ background: color }}
                aria-label={colorNames[index] ?? color}
                title={colorNames[index] ?? color}
                onClick={(event) => {
                  // Alt+clic pose la couleur de remplissage, comme le clic droit.
                  if (event.altKey) setSecondary(secondary === color ? null : color);
                  else setPrimary(color);
                }}
                onContextMenu={(event) => {
                  event.preventDefault();
                  setSecondary(secondary === color ? null : color);
                }}
              />
            ))}
          </div>

          <span className={styles.status}>{statusText}</span>
          <span className={cx(styles.status, styles.statusCell)} ref={coordsRef} />
          <span className={cx(styles.status, styles.statusCell)} ref={shapeRef} />
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className={styles.fileInput}
        onChange={(event) => {
          openImage(event.currentTarget.files?.[0]);
          event.currentTarget.value = '';
        }}
      />

      {dialog !== null && (
        <div className={styles.dialogLayer} role="dialog" aria-modal="true">
          <div className={styles.dialog}>
            <div className={styles.titleBar}>
              <span>
                {t(
                  dialog === 'about'
                    ? S.paint.dialogs.about_title
                    : dialog === 'colors'
                      ? S.paint.menus.colors_edit
                      : dialog === 'help'
                        ? S.paint.menus.help_f1
                        : dialog === 'quit'
                          ? S.paint.menus.file_quit
                          : S.paint.menus.file_new,
                  lang,
                )}
              </span>
            </div>

            <div className={styles.dialogBody}>
              {dialog === 'colors' ? (
                <>
                  <div className={styles.dialogPalette}>
                    {PALETTE.map((color, index) => (
                      <button
                        key={color}
                        type="button"
                        className={cx(styles.bigSwatch, primary === color && styles.swatchActive)}
                        style={{ background: color }}
                        aria-label={colorNames[index] ?? color}
                        onClick={() => {
                          setPrimary(color);
                          setHexDraft(color);
                        }}
                      />
                    ))}
                  </div>
                  <label className={styles.hexRow}>
                    {t(S.paint.colors.hex, lang)}
                    <input
                      type="text"
                      className={styles.hexInput}
                      value={hexDraft}
                      onChange={(event) => {
                        const next = event.currentTarget.value;
                        setHexDraft(next);
                        if (isHexColor(next)) setPrimary(normalizeHex(next));
                      }}
                    />
                  </label>
                </>
              ) : (
                <p className={styles.dialogText}>
                  {t(
                    dialog === 'about'
                      ? S.paint.dialogs.about_body
                      : dialog === 'help'
                        ? S.paint.dialogs.help_body
                        : dialog === 'quit'
                          ? S.paint.dialogs.quit_body
                          : S.paint.dialogs.new_confirm,
                    lang,
                  )}
                </p>
              )}
            </div>

            <div className={styles.dialogButtons}>
              {dialog === 'new' && (
                <button
                  type="button"
                  className={styles.chromeButton}
                  onClick={() => {
                    setDialog(null);
                  }}
                >
                  {t(S.paint.dialogs.cancel, lang)}
                </button>
              )}
              <button
                type="button"
                className={styles.chromeButton}
                onClick={() => {
                  if (dialog === 'new') paint.clear();
                  setDialog(null);
                }}
              >
                {t(S.paint.dialogs.ok, lang)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
